import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, FooterButton } from 'Core/components';
import CustomPropTypes from 'Core/prop-types';
import { addMoldRelease, editMoldRelease } from './store/slice';
import { AppModal } from '../components/app-modals';
import { showToast } from 'Core/redux/toast/slice';
import MoldReleaseAddEditForm from './mold-release-add-edit-form';
import { useDocumentUpload } from '../../core/hooks';
import { MOLD_RELEASE } from '../../core/constants/moldConstants';

const MoldReleaseAddEdit = ({ showModal, modalData, onCloseModal }) => {
  const [formData, setFormData] = useState(modalData || null);
  const [tempFiles, setTempFile] = useState([]);
  const { uploadDocuments, loading } = useDocumentUpload();

  const dispatch = useDispatch();
  const isEditMode = Boolean(modalData?.moldReleaseData?.toolID);


  const onFileUpload = (documentName, file) => {
    if (file) {
      setTempFile(prevFiles => {
        const updatedFiles = prevFiles.map(f =>
          f.documentName === documentName ? { ...f, file } : f,
        );

        // If documentName exists, update it; otherwise, add new file
        const isExisting = prevFiles.some(f => f.documentName === documentName);

        return isExisting ? updatedFiles : [...prevFiles, { file, documentName }];
      });
    }
  };

  const onChangesMoldReleaseForm = data => {
    setFormData(prevData => ({
      ...data,
      isReleased: prevData?.isReleased || false // Preserve isReleased flag
    }));
  };

  const handleClose = () => {
    onCloseModal();
  };

  const body = () => {
    return (
      <MoldReleaseAddEditForm
        data={formData}
        onChanges={onChangesMoldReleaseForm}
        onFileUpload={onFileUpload}
      />
    );
  };

  const valid = () => {
    if (formData?.outsourcing === '') {
      //|| partsData?.length === 0) {
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        }),
      );
      return false;
    }
    return true;
  };

  const footer = () => {
    // Check if this is a released mold (read-only mode)
    const isReleased = formData?.isReleased || false;
    // Map upload constants to backend property names
    const uploadFieldMap = {
      OPEN_ONE: 'Open1',
      OPEN_TWO: 'Open2',
      OPEN_THREE: 'Open3',
      OPEN_FOUR: 'Open4',
      PICTURE_ONE: 'Picture1',
      PICTURE_TWO: 'Picture2',
      PICTURE_THREE: 'Picture3',
      PICTURE_FOUR: 'Picture4',
      LOADED_ONE: 'Loaded1',
      LOADED_TWO: 'Loaded2',
      BOL_SIGNED_COPY: 'BOL_Signed_Copy',
      PACKING_SLIP_SIGNED_COPY: 'PackingSlip_Signed_Copy',
    };
    const onSave = async () => {
      if (!valid()) return;
      for (const tFile of tempFiles) {
        const docFormData = new FormData();
        docFormData.append('file', tFile.file);
        docFormData.append('fileCategoryId', tFile.documentName);
        docFormData.append('uploadCategory', `MOLD${formData.toolID}`);

        try {
          const result = await uploadDocuments(docFormData);
          
          // Use the mapping to set the correct backend property name
          const field = uploadFieldMap[tFile.documentName];
          if (field) {
            formData[field] = result.relativePath;
          }
        } catch (error) {
          // File upload failed
        }
      }

      // Send as camelCase object, not array, not PascalCase
      // Use the correct moldInfoID from draft response, and include draft ID for updating
      const finalFormData = {
        ...formData,
        // For final submit:
        // - If we have a draft, use the draft's moldInfoID (the actual mold receiving ID)
        // - If no draft, use the original moldInfoID from the form
        // - Always include the draft ID if it exists so the API can update the existing draft
        moldInfoID: formData.draftMoldInfoID || formData.moldInfoID || 0,
        // Include draft ID if it exists so the API knows to update existing record
        id: formData.draftId && formData.draftId > 0 ? formData.draftId : 0
      };
      
      
      if (finalFormData.moldInfoID == 0) {
        dispatch(addMoldRelease(finalFormData));
      } else {
        dispatch(editMoldRelease(finalFormData));
      }
    };

    return (
      <div className="row">
        <div className="col-lg-8"></div>
        <div className="col-lg-4">
          <FooterButton
            cancelText="Cancel"
            saveText="Submit "
            onSave={onSave}
            onCancel={handleClose}
            disableSave={isReleased}
            disableCancel={isReleased}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="mold-release-modal"
      show={showModal}
      size="xl"
      title={isEditMode ? 'Edit Mold Release' : 'Add Mold Release'}
      body={body}
      footer={footer}
      handleClose={handleClose}
    />
  );
};

MoldReleaseAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.shape(CustomPropTypes.MoldRelease),
  onCloseModal: PropTypes.func,
};

export default AppModal(MoldReleaseAddEdit);
